/**
 * SF-5080: Suggest (anyone who can use priorities). Send an idea, optionally for one list, and
 * see what happened to each of mine. Suggestions are never ranked until a manager accepts one.
 * The list field follows the list the page shows until the person picks one themselves.
 *
 * SF-5087: the person can also pick the companies that want it (active Paid and Demo only).
 */
import { useEffect, useState } from 'react';
import { App, Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatShortDate } from '../../../utils/date';
import {
  createSuggestionArgs,
  prioritiesErrorMessage,
  useMySuggestions,
  usePrioritiesWrite,
  type MySuggestion,
  type PriorityList,
} from '../api/prioritiesClient';
import { ITEM_DESCRIPTION_MAX, ITEM_TITLE_MAX } from '../constants';
import { LoadError, RefreshWarning } from './LoadError';
import { CompanySelect, CompanyTags } from './CompanySelect';

interface Values {
  title: string;
  description?: string;
  listId: string;
  companyIds?: string[];
}

const suggestionStatusTag = (s: MySuggestion) => {
  if (s.status === 'suggested') return <Tag color="processing">Waiting</Tag>;
  if (s.status === 'merged')
    return <Tag color="success">Folded into {s.item?.title ?? 'another item'}</Tag>;
  if (s.status === 'declined') return <Tag>Declined</Tag>;
  return <Tag color="success">Accepted</Tag>;
};

export const SuggestTab: React.FC<{ lists: PriorityList[]; listId: string }> = ({
  lists,
  listId,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<Values>();
  const write = usePrioritiesWrite();
  const mineQuery = useMySuggestions();
  const mine = mineQuery.data?.pages.flatMap((page) => page.suggestions) ?? [];
  // Suggestions can go to a list that is open or not open yet, never an archived one.
  const listOptions = lists
    .filter((l) => l.status !== 'archived')
    .map((l) => ({ value: l.id, label: l.name }));
  const listName = (id?: string) => lists.find((l) => l.id === id)?.name ?? '';
  // The page's list, when a suggestion can go to it.
  const pageList = listOptions.some((o) => o.value === listId) ? listId : undefined;
  // Whether the person picked the list themselves; until then it follows the page's list (the
  // form's initial values apply only once), and the typed title and description stay as they are.
  const [listPicked, setListPicked] = useState(false);
  useEffect(() => {
    if (!listPicked) form.setFieldValue('listId', pageList);
  }, [form, listPicked, pageList]);

  const submit = async (values: Values) => {
    try {
      await write.run(
        createSuggestionArgs({
          title: values.title.trim(),
          ...(values.description?.trim() ? { description: values.description.trim() } : {}),
          ...(values.listId ? { listId: values.listId } : {}),
          ...(values.companyIds?.length ? { companyIds: values.companyIds } : {}),
        }),
      );
      form.resetFields();
      setListPicked(false);
      form.setFieldValue('listId', pageList);
      void message.success('Thanks, your suggestion is sent');
    } catch (error) {
      void message.error(prioritiesErrorMessage(error, 'Your suggestion could not be sent.'));
    }
  };

  const columns: ColumnsType<MySuggestion> = [
    {
      title: 'Suggestion',
      dataIndex: 'title',
      render: (title: string, s) => (
        <Space direction="vertical" size={2}>
          {title}
          <CompanyTags companies={s.companies} />
        </Space>
      ),
    },
    { title: 'List', dataIndex: 'listId', width: 200, render: listName },
    { title: 'Sent', dataIndex: 'createdAt', width: 140, render: formatShortDate },
    { title: 'Status', key: 'status', width: 260, render: (_, s) => suggestionStatusTag(s) },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" title="Suggest an item">
        <Form
          form={form}
          layout="vertical"
          onFinish={submit}
          initialValues={{ listId: pageList }}
          onValuesChange={(changed: Partial<Values>) => {
            if ('listId' in changed) setListPicked(true);
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, whitespace: true, message: 'Give your suggestion a title' },
              { max: ITEM_TITLE_MAX, message: `At most ${ITEM_TITLE_MAX} characters` },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[
              { max: ITEM_DESCRIPTION_MAX, message: `At most ${ITEM_DESCRIPTION_MAX} characters` },
            ]}
          >
            <Input.TextArea rows={3} placeholder="What should we build, and why?" />
          </Form.Item>
          {listOptions.length > 0 && (
            <Form.Item name="listId" label="Which list is it for (optional)">
              <Select allowClear options={listOptions} placeholder="Not sure" />
            </Form.Item>
          )}
          <Form.Item name="companyIds" label="Companies that want this (optional)">
            <CompanySelect />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={write.isLoading}>
            Send suggestion
          </Button>
        </Form>
      </Card>
      <Card size="small" title="My suggestions">
        {mineQuery.isError && !mineQuery.data ? (
          <LoadError
            error={mineQuery.error}
            fallback="Your suggestions could not be loaded."
            onRetry={mineQuery.refetch}
          />
        ) : (
          <>
            {mineQuery.isError && (
              <RefreshWarning
                error={mineQuery.error}
                fallback="Your suggestions could not be refreshed."
                onRetry={mineQuery.refetch}
              />
            )}
            <Table<MySuggestion>
              rowKey="id"
              loading={mineQuery.isLoading}
              columns={columns}
              dataSource={mine}
              pagination={false}
              locale={{ emptyText: 'You have not suggested anything yet' }}
            />
            {/* sf-api sends 50 at a time, newest first; older ones are read on asking. */}
            {mineQuery.hasNextPage && (
              <Button
                style={{ marginTop: 8 }}
                loading={mineQuery.isFetchingNextPage}
                onClick={() => void mineQuery.fetchNextPage()}
              >
                Load more
              </Button>
            )}
          </>
        )}
      </Card>
    </Space>
  );
};
